// src/services/e2ee.service.ts
//
// End-to-end encryption for file attachments. AES-256-GCM in the browser; the
// key lives ONLY in the URL fragment (never sent to the server). The server
// stores and moves only ciphertext it cannot read.
//
// Phase 1 encrypts the whole file in one shot (files fit in memory under the
// size cap). Chunked/streaming AEAD for very large files is a later phase.

const ALGO = 'AES-GCM';
const IV_BYTES = 12; // 96-bit nonce, the AES-GCM standard

function toBase64Url(input: ArrayBuffer | Uint8Array): string {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(str: string): Uint8Array {
  const normalized = str.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '==='.slice((normalized.length + 3) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** Generate a fresh, extractable AES-256-GCM key. */
export async function generateKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey({ name: ALGO, length: 256 }, true, ['encrypt', 'decrypt']);
}

/** Serialize the key for the URL fragment (base64url of the raw key bytes). */
export async function exportKeyToFragment(key: CryptoKey): Promise<string> {
  const raw = await crypto.subtle.exportKey('raw', key);
  return toBase64Url(raw);
}

/** Recover the key from a URL fragment value. */
export async function importKeyFromFragment(fragment: string): Promise<CryptoKey> {
  const keyData = fromBase64Url(fragment) as unknown as BufferSource;
  return crypto.subtle.importKey('raw', keyData, { name: ALGO }, true, ['encrypt', 'decrypt']);
}

/**
 * Encrypt bytes and return a single blob: IV (12 bytes) || ciphertext+tag.
 * The random IV is fresh per call, so the same key/plaintext never repeats.
 */
export async function encryptBytes(key: CryptoKey, data: ArrayBuffer): Promise<ArrayBuffer> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ciphertext = await crypto.subtle.encrypt({ name: ALGO, iv }, key, data);
  const out = new Uint8Array(IV_BYTES + ciphertext.byteLength);
  out.set(iv, 0);
  out.set(new Uint8Array(ciphertext), IV_BYTES);
  return out.buffer;
}

/** Reverse of {@link encryptBytes}: split the IV, decrypt, return plaintext. */
export async function decryptBytes(key: CryptoKey, blob: ArrayBuffer): Promise<ArrayBuffer> {
  const all = new Uint8Array(blob);
  const iv = all.subarray(0, IV_BYTES);
  const ciphertext = all.subarray(IV_BYTES);
  return crypto.subtle.decrypt({ name: ALGO, iv }, key, ciphertext);
}

/**
 * Encrypt a File end-to-end. Returns the ciphertext blob to upload and the
 * fragment key to put in the link. Only the file CONTENT is encrypted; the
 * filename/type travel as plaintext server metadata (the owner + admin views
 * need them), so they are NOT part of the ciphertext here.
 */
export async function encryptFile(file: File): Promise<{
  ciphertext: ArrayBuffer;
  fragmentKey: string;
}> {
  const key = await generateKey();
  const ciphertext = await encryptBytes(key, await file.arrayBuffer());
  const fragmentKey = await exportKeyToFragment(key);
  return { ciphertext, fragmentKey };
}

/**
 * Decrypt a downloaded ciphertext blob with the fragment key, recovering the
 * original file bytes. The filename/type come from the (plaintext) server
 * response, not from the ciphertext.
 */
export async function decryptFile(
  fragmentKey: string,
  ciphertext: ArrayBuffer
): Promise<ArrayBuffer> {
  const key = await importKeyFromFragment(fragmentKey);
  return decryptBytes(key, ciphertext);
}
